import { useCallback, useEffect, useState } from "react";
import { MessagesService } from "../../../../../services/messages";
import { IMessage } from "../../../types/Message";

export const useFetchMessages = (
  parentId: string | undefined,
  contractNumber: number | undefined,
  unreads: IMessage[],
  setConversationId: React.Dispatch<React.SetStateAction<string | undefined>>,
  setCanLoadMore: React.Dispatch<React.SetStateAction<boolean>>,
  setInitErr: React.Dispatch<React.SetStateAction<string | null>>,
  setInitData?: React.Dispatch<React.SetStateAction<any>>
) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [data, setData] = useState<IMessage[]>([]);

  const initialize = useCallback(async () => {
    try {
      setIsLoading(true);

      const res: any = await MessagesService.initialize({ contractNumber });

      if (res && res.messages) {
        setData(res.messages);
        setConversationId(res.id);

        res.messages.length < 50 && setCanLoadMore(false);
      }

      setInitData &&
        setInitData({
          isClosed: res.isClosed,
          isArchived: res.isArchived,
        });

      setIsLoading(false);
    } catch (e: any) {
      setInitErr(e.response.data.title);
      setIsLoading(false);
    }
  }, [
    contractNumber,
    setConversationId,
    setCanLoadMore,
    setInitErr,
    setInitData,
  ]);

  const getMessages = useCallback(async () => {
    try {
      setIsLoading(true);

      const res: any = await MessagesService.fetchMessages(
        { take: 50 },
        parentId || ""
      );

      setData(res);

      res.length < 50 && setCanLoadMore(false);

      setConversationId(parentId);

      setIsLoading(false);
    } catch (e: any) {
      setInitErr(e.response.data.title);
      setIsLoading(false);
    }
  }, [parentId, setConversationId, setCanLoadMore, setInitErr]);

  const fetchData = useCallback(async () => {
    if (parentId) {
      await getMessages();
    } else {
      await initialize();
    }
  }, [parentId, getMessages, initialize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    unreads.length > 0 && setData((prev: IMessage[]) => [...prev, ...unreads]);
  }, [unreads]);

  return { data, isLoading, setData };
};
